import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Dexie from 'dexie'; // eslint-disable-line import/no-named-as-default

import { LT_POS } from 'bl/loggers.bl';
import SENTRY from 'config/sentry.singleton';
import MS from 'constants/time/ms.const';
import alen from 'utils/arrays/alen.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { stringifyCustomJson } from 'utils/converters/json.util';
import fromEntriesToObject from 'utils/converters/object/fromEntriesToObject.util';
import fromObjectToEntries from 'utils/converters/object/fromObjectToEntries.util';
import fromObjectToKeys from 'utils/converters/object/fromObjectToKeys.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { nowAsIso, nowAsTimestamp } from 'utils/date/now.util';
import { isEnvDev, isEnvLab, isEnvQA, isEnvStage } from 'utils/env/determine.util';
import loop from 'utils/functions/loop.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import olen from 'utils/objects/olen.util';
import parseJson from 'utils/parsers/parseJson';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFullObject from 'utils/predicates/object/isFullObject.util';
import { isTimeBefore } from 'utils/predicates/time/compare.util';
import generateId from 'utils/values/generateId.util';

const CTAG = 'PaymentAuditProvider';
const L = logger([CTAG, LT_POS]);

const IS_STAGE = isEnvStage();
const IS_QA = isEnvQA();
const IS_DEV = isEnvDev();
const IS_LAB = isEnvLab();

const USE_SPAM = false;
const USE_AUDIT_LOOP_ERROR = false;

const Context = React.createContext();

const DB_NAME = `SlickPaymentAudit`;
const DB_SCHEMA_CHECKOUT = 'Checkout';
const DB_VERSION = 1;

const STALE_LIMIT = IS_STAGE || IS_QA || IS_DEV || IS_LAB ? 10 * MS.minute : MS.hour;
const FRESH_LIMIT = IS_STAGE || IS_QA || IS_DEV || IS_LAB ? 2 * MS.minute : 5 * MS.minute;
const LOOP_DELAY = IS_STAGE || IS_QA || IS_DEV || IS_LAB ? MS.minute : 10 * MS.minute;

const isDifferentValue = (newVal, oldVal) => {
  if (Array.isArray(oldVal) && Array.isArray(newVal)) {
    //
    if (oldVal.length !== newVal.length) return true;
    //
    for (let i = 0; i < oldVal.length; i += 1) {
      if (isDifferentValue(oldVal[i], newVal[i])) return true;
    }
    //
  } else if (0 === newVal && 0 === oldVal) {
    return false;
  } else {
    if (!Object.is(oldVal, newVal)) return true;
  }
  return false;
};

const isDifferentData = (newOne, oldOne) => {
  for (const key of fromObjectToKeys(newOne ?? {})) {
    if (isDifferentValue(newOne[key], oldOne?.[key])) {
      return true;
    }
  }
  return false;
};

const ENDS = Object.freeze({
  fail: '.fail',
  warn: '.warn',
  error: '.error',
});

const createReplacer = (options) => {
  const keepFunctions = options?.keepFunctions;
  const keepErrors = options?.keepErrors;
  const replaceUndefined = options?.replaceUndefined;

  return (key, val) => {
    if (replaceUndefined && void 1 === val) return null;

    if (keepFunctions && 'function' === typeof val) {
      const name = String(val?.name ?? '').trim() || '<anonymous>';
      return `|[function:${name}]|`;
    }

    if (keepErrors && val instanceof Error) {
      return {
        e$string: String(val),
        e$type: String(val?.constructor?.name),
        e$name: String(val?.name),
        e$message: String(val?.message),
        e$stack: String(val?.stack),
      };
    }

    return val;
  };
};
const REPLACER = createReplacer({ replaceUndefined: true, keepErrors: true });

const checkIfInteresting = (record) => {
  const logLines = asArray(record?.log);

  for (let index = 0; index < logLines.length; index += 1) {
    const line = logLines[index];

    const cp = fromAnyToTrimmed(line?.checkpoint);
    const data = line?.data;
    const dataJson = stringifyCustomJson({ value: data, replacer: REPLACER });

    if (cp.endsWith(ENDS.fail)) return { index, type: 'failCheckpoint', checkpoint: cp, dataJson };
    if (cp.endsWith(ENDS.warn)) return { index, type: 'warnCheckpoint', checkpoint: cp, dataJson };

    if (cp.endsWith(ENDS.error)) {
      const error = data?.error$next ?? data?.error$prev ?? data?.error;
      return { index, type: 'errorCheckpoint', checkpoint: cp, error, dataJson };
    }
  }

  const errorInContext = record?.context?.error;
  if (errorInContext) return { type: 'errorInContext', error: errorInContext };

  if (false === record?.context?.result?.is?.ok) return { type: 'okInContext', is: record?.context?.result?.is };

  const errorMessageInContext = record?.context?.errorMessage;
  if (fromAnyToTrimmed(errorMessageInContext)) return { type: 'errorMessageInContext' };

  const errorInCombined = record?.combined?.error;
  if (errorInCombined) return { type: 'errorInCombined', error: errorInCombined };

  if (false === record?.combined?.result?.is?.ok) return { type: 'okInCombined', is: record?.combined?.result?.is };

  const errorMessageInCombined = record?.combined?.errorMessage;
  if (fromAnyToTrimmed(errorMessageInCombined)) return { type: 'errorMessageInCombined' };

  return false;
};

const transmitRecords = (options) => {
  //
  const raw = options?.value;
  const tags = options?.tags ?? {};
  const ids = [];

  try {
    for (const record of asArray(raw)) {
      //
      const x = record?.context;
      const b = record?.combined;

      SENTRY.warn({
        logger: CTAG,
        tags: {
          ...tags,
          salonId: x?.salonId ?? b?.salonId,
          clientId: x?.clientId ?? b?.clientId,
          appointmentId: x?.appointmentId ?? b?.appointmentId,
          paymentIntentId: x?.paymentIntentId ?? b?.paymentIntentId,
          createdAt: record?.createdAt,
          modifiedAt: record?.modifiedAt,
        },
        extra: record,
        message: 'Checkout payment of interest',
      });

      ids.push(record?.id);
    } // for

    return { raw, ids, count: ids.length };
    //
  } catch (error) {
    L.warn('transmitRecords()', error);
    return { raw, error, count: 0 };
  }
};

const idOf = (item) => item?.id;

const refineRecords = (options) => {
  const raw = options?.value;

  const interesting = [];
  const undetermined = [];
  const trivial = [];

  for (const record of asArray(raw)) {
    const reason = checkIfInteresting(record);
    if (reason) {
      interesting.push({
        ...record,
        reason,
        // repackage the log so it appears easier to navigate in Sentry
        log: fromEntriesToObject(asArray(record.log).map((item, index) => [index, item])),
      });
    } else if (isTimeBefore(record.createdAt, nowAsTimestamp() - FRESH_LIMIT)) {
      undetermined.push(record);
    } else {
      trivial.push(record);
    }
  }

  return {
    raw,
    interesting,
    interestingCount: interesting.length,
    interestingIds: interesting.map(idOf),
    trivial,
    trivialCount: trivial.length,
    trivialIds: trivial.map(idOf),
    undetermined,
    undeterminedCount: undetermined.length,
    undeterminedIds: undetermined.map(idOf),
  };
};

export const PaymentAuditProvider = (props) => {
  const dbName = fromAnyToTrimmed(props.name) || DB_NAME;
  const dbVersion = fromAnyToInteger(props.dbVersion) || DB_VERSION;
  const schemaCheckout = fromAnyToTrimmed(props.schemaCheckout) || DB_SCHEMA_CHECKOUT;

  const memoizedValue = useMemo(() => {
    try {
      const db = new Dexie(dbName);

      db.version(dbVersion).stores({ [schemaCheckout]: '&id,createdAt,modifiedAt' });

      return { checkoutTable: db[schemaCheckout] };
    } catch (e) {
      L.warn('()', e);
      return {};
    }
  }, [dbName, dbVersion, schemaCheckout]);

  const checkoutTable = memoizedValue.checkoutTable;

  useEffect(() => {
    const FTAG = 'loop()';
    let shouldContinue = true;

    loop(
      {
        block: true,
        delay: LOOP_DELAY,
        onInit: USE_SPAM ? () => void L.debug(FTAG, 'onInit()', 'shouldContinue:', shouldContinue) : void 1,
        onDone: USE_SPAM ? () => void L.debug(FTAG, 'onDone()', 'shouldContinue:', shouldContinue) : void 1,
        onTest: () => shouldContinue,
      },
      async () => {
        try {
          const { interesting, trivialIds, interestingCount, trivialCount, undeterminedIds } = refineRecords({
            value: await checkoutTable.toArray(),
          });

          USE_SPAM && L.debug(FTAG, 'refined records', ...L.O({ interestingCount, trivialCount, undeterminedIds }));

          const { ids, count } = transmitRecords({ value: interesting, tags: { table: DB_SCHEMA_CHECKOUT } });

          if (count) {
            if (USE_SPAM) L.debug(FTAG, 'transmitted ids:', ids, 'interesting:', interesting);
            else L.debug(FTAG, 'transmitted ids:', ids);

            L.info(FTAG, 'deleting transmitted ids:', ids, '...');
            await checkoutTable.bulkDelete(ids);
            L.info(FTAG, 'deleting transmitted records done!');
          }

          if (trivialCount) {
            L.info(FTAG, 'deleting trivial ids:', trivialIds, '...');
            await checkoutTable.bulkDelete(trivialIds);
            L.info(FTAG, 'deleting trivial records done!');
          }

          const staleIds = await checkoutTable
            .where('createdAt')
            .below(fromAnyDateToIso(nowAsTimestamp() - STALE_LIMIT))
            .toArray((collection) => collection.map((item) => item?.id));

          if (isFullArray(staleIds)) {
            L.info(FTAG, 'deleting stale ids:', staleIds, '...');
            await checkoutTable.bulkDelete(staleIds);
            L.info(FTAG, 'deleting stale records done!');
          }
        } catch (error) {
          L.warn(FTAG, error);
          if (USE_AUDIT_LOOP_ERROR) SENTRY.error({ logger: [CTAG, FTAG], error, message: 'Audit loop error' });
        }
      },
    );

    return () => void (shouldContinue = false);
  }, [checkoutTable]);

  return <Context.Provider value={memoizedValue}>{props.children}</Context.Provider>;
};

export const usePaymentAudit = (options) => {
  const FTAG = 'usePaymentAudit()';

  const { checkoutTable } = useContext(Context);

  const [checkoutId, setCheckoutId] = useState('');
  const checkoutRecordRef = useRef(null);

  const enabled = options.enabled; // this can be anything, true, false, undefined, 'banana'...
  const disabled = !enabled; // but this is always true or false, use it to limit the number of re-renders
  useMemo(() => L.info(FTAG, 'is enabled:', enabled), [enabled]);

  useMemo(() => {
    if (disabled) return;

    // the purpose of this function is to create the initial empty object and get the ID for future use
    // if there already is an ID, it means it's already created, don't do it again
    if (checkoutId) return;

    const id = generateId();
    setCheckoutId(id);

    const create = async () => {
      // just create the record, don't add more data
      L.info(FTAG, 'creating', id, '...');
      checkoutRecordRef.current = { id, createdAt: nowAsIso() };

      await checkoutTable.upsert(id, checkoutRecordRef.current);
    };

    L.unignore(FTAG, 'create:', create());
  }, [disabled, checkoutId, checkoutTable]);

  const add = useCallback(
    async (options) => {
      if (disabled) return;
      if (!checkoutId) return; // Not yet created

      const old = checkoutRecordRef.current;

      const cp = fromAnyToTrimmed(options?.checkpoint);
      const tag = fromAnyToTrimmed(options?.data?.tag);

      const contextData = {};
      const latestData = {};
      const indices = {};
      const length = alen(asArray(old?.log));

      if (!cp && !isFullObject(options?.data)) {
        return void L.error(FTAG, 'add()', checkoutId, 'Empty checkpoint:', cp, 'and data:', options?.data);
      }

      const clonedData = parseJson(stringifyCustomJson({ replacer: REPLACER, value: options?.data ?? {} }));
      for (const [key, val] of fromObjectToEntries(clonedData)) {
        const prevContextValue = old?.context?.[key];
        const prevIndicesArray = old?.indices?.[key] ?? [];

        if (isDifferentValue(val, prevContextValue)) {
          latestData[`${key}$prev`] = prevContextValue;
          latestData[`${key}$next`] = val;
          contextData[key] = val;
          indices[key] = [...prevIndicesArray, length];
        }
      } // for

      const now = nowAsIso();

      const latest = {};

      if (cp) latest.checkpoint = cp;
      if (tag) latest.tag = tag;
      if (isFullObject(latestData)) latest.data = latestData;
      latest.createdAt = now;

      const log = [...asArray(old?.log), latest];

      checkoutRecordRef.current = parseJson(
        stringifyCustomJson({
          replacer: REPLACER,
          value: {
            ...asObject(old),
            checkpoint: cp || now,
            modifiedAt: now,
            logSize: log.length,
            lastTagged: cp ? latest : old?.lastTagged ?? null,
            lastLogged: latest,
            context: cp ? old?.context ?? null : { ...asObject(old?.context), ...contextData },
            combined: { ...asObject(old?.combined), ...contextData },
            indices: { ...asObject(old?.indices), ...indices },
            log,
          },
        }),
      );

      await checkoutTable.update(checkoutId, checkoutRecordRef.current);
    },
    [disabled, checkoutId, checkoutTable],
  );

  const checkpoint = useCallback(
    (tag, ...extra) => {
      if (disabled) return;

      const FTAG = 'checkpoint()';

      const data = {};
      if (extra.length) {
        for (const arg of extra) {
          for (const [key, val] of fromObjectToEntries(arg ?? {})) {
            data[key] = val;
          }
        }
      }

      const record = checkoutRecordRef.current;
      const size = olen(data);
      const cp = fromAnyToTrimmed(tag);

      if (cp === record?.checkpoint && !size) {
        USE_SPAM && L.debug(FTAG, 'Same checkpoint:', cp, 'and no data to save. Not updating:', checkoutId);
        return;
      }

      if (cp === record && !isDifferentData(data, record?.context)) {
        USE_SPAM && L.debug(FTAG, 'Same checkpoint:', cp, 'and same data to save. Not updating:', checkoutId);
        return;
      }

      add({ checkpoint: cp, data })
        .then(() => {
          USE_SPAM && L.debug(FTAG, 'Updated:', checkoutId, 'to checkpoint:', cp, 'with data:', data);
        })
        .catch((e) => {
          L.error(FTAG, 'Failed to update:', checkoutId, 'to checkpoint:', cp, 'with data:', data, e);
        });
    },
    [add, checkoutId, disabled],
  );

  return useMemo(() => (disabled ? { checkpoint: alwaysNoop, id: '' } : { checkpoint, id: checkoutId }), [checkoutId, checkpoint, disabled]);
};
